import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { computeLineDiff } from "lib/lineDiff";
import {
    ACTIVE_PROFILE_FILE,
    fetchNav2Health,
    fetchNav2NavStatus,
    fetchNav2ParamsActive,
    fetchNav2ParamsProfile,
    fetchNav2ParamsProfiles,
    getNav2ParamsApiBaseUrl,
    isSelectedProfileActive,
    navServiceDisplayState,
    postNav2NavRestart,
    deleteNav2ParamsProfile,
    postNav2ParamsActivate,
    postNav2ParamsProfile,
    postNav2ParamsRename,
    postNav2ParamsRestoreDefault,
    profileFileName,
    profileNameStem,
    putNav2ParamsActive,
    putNav2ParamsNamedProfile,
    resolveActiveProfileName,
    selectionForActiveProfile,
    storedProfiles,
    type Nav2ParamsProfile,
    type NavServiceDisplayState,
} from "lib/nav2ParamsApi";
import useFleetArcId from "lib/utils/useFleetArcId";
import useIsHttpSecure from "lib/utils/useIsHttpSecure";
import { useARC } from "providers/ARC/ARCProvider";
import { useToast } from "providers/ToastProvider";

const MAX_SHOWN_DIFF_LINES = 80;

const DeveloperNav2ParamsPanel: React.FC = () => {
    const { addToast } = useToast();
    const { selectedARC } = useARC();
    const isSecure = useIsHttpSecure();
    const arcId = useFleetArcId();
    const baseUrl = useMemo(
        () => getNav2ParamsApiBaseUrl(selectedARC?.host ?? null, isSecure, arcId),
        [selectedARC?.host, isSecure, arcId]
    );

    const [apiReady, setApiReady] = useState(false);
    const [profiles, setProfiles] = useState<Nav2ParamsProfile[]>([]);
    const [selectedName, setSelectedName] = useState("");
    const [activeProfileName, setActiveProfileName] = useState<string | null>(null);
    const [draft, setDraft] = useState("");
    const [savedContent, setSavedContent] = useState("");
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [navServiceState, setNavServiceState] = useState<NavServiceDisplayState>("unknown");
    const [error, setError] = useState<string | null>(null);
    const loadToken = useRef(0);
    const baseUrlRef = useRef(baseUrl);
    const selectedNameRef = useRef(selectedName);
    const profilesRef = useRef(profiles);
    const activeProfileNameRef = useRef(activeProfileName);
    baseUrlRef.current = baseUrl;
    selectedNameRef.current = selectedName;
    profilesRef.current = profiles;
    activeProfileNameRef.current = activeProfileName;

    const dropdownProfiles = useMemo(() => storedProfiles(profiles), [profiles]);
    const refreshNavStatus = useCallback(async () => {
        const url = baseUrlRef.current;
        if (!url) return;
        try {
            const status = await fetchNav2NavStatus(url);
            setNavServiceState(navServiceDisplayState(status));
        } catch {
            setNavServiceState((prev) => (prev === "unknown" ? "unknown" : prev));
        }
    }, []);
    const dirty = draft !== savedContent;
    const isActiveSelection = isSelectedProfileActive(selectedName, activeProfileName);
    const lineDiff = useMemo(
        () => (dirty ? computeLineDiff(savedContent, draft) : []),
        [dirty, draft, savedContent]
    );
    const visibleLineDiff = useMemo(
        () => lineDiff.slice(0, MAX_SHOWN_DIFF_LINES),
        [lineDiff]
    );
    const hiddenDiffCount = lineDiff.length - visibleLineDiff.length;

    const loadProfileContent = useCallback(
        async (
            name: string,
            options?: {
                profiles?: Nav2ParamsProfile[];
                activeProfileName?: string | null;
            }
        ) => {
            const url = baseUrlRef.current;
            if (!url) return;

            const knownProfiles = options?.profiles ?? profilesRef.current;
            let activeName = options?.activeProfileName ?? activeProfileNameRef.current;
            let selection = name;

            if (selection === ACTIVE_PROFILE_FILE) {
                selection = selectionForActiveProfile(knownProfiles, activeName);
            }

            const token = ++loadToken.current;
            setLoading(true);
            setError(null);
            try {
                const useActiveEndpoint =
                    selection === ACTIVE_PROFILE_FILE ||
                    (activeName !== null && profileNameStem(selection) === activeName);
                const res = useActiveEndpoint
                    ? await fetchNav2ParamsActive(url)
                    : await fetchNav2ParamsProfile(url, selection);
                if (token !== loadToken.current) return;

                const resolvedActive =
                    resolveActiveProfileName(knownProfiles, res) ?? activeName;
                setActiveProfileName(resolvedActive);
                setDraft(res.content);
                setSavedContent(res.content);
                setSelectedName(
                    selection === ACTIVE_PROFILE_FILE
                        ? selectionForActiveProfile(knownProfiles, resolvedActive)
                        : selection
                );
            } catch (e) {
                if (token !== loadToken.current) return;
                setError(e instanceof Error ? e.message : "Failed to load params");
            } finally {
                if (token === loadToken.current) setLoading(false);
            }
        },
        []
    );

    const reloadAll = useCallback(
        async (profileName?: string) => {
            const url = baseUrlRef.current;
            const requestedName = profileName ?? selectedNameRef.current;
            if (!url) {
                setApiReady(false);
                setError("Nav2 params API URL unavailable");
                return;
            }
            setLoading(true);
            setError(null);
            try {
                await fetchNav2Health(url);
                setApiReady(true);
                const list = await fetchNav2ParamsProfiles(url);
                setProfiles(list.profiles);

                const resolvedActive = resolveActiveProfileName(list.profiles);
                if (resolvedActive) {
                    setActiveProfileName(resolvedActive);
                }

                const loadName =
                    requestedName === ACTIVE_PROFILE_FILE || !requestedName
                        ? selectionForActiveProfile(list.profiles, resolvedActive)
                        : requestedName;

                await loadProfileContent(loadName, {
                    profiles: list.profiles,
                    activeProfileName: resolvedActive,
                });

                await refreshNavStatus();
            } catch (e) {
                setApiReady(false);
                const msg = e instanceof Error ? e.message : "Failed to load Nav2 params";
                setError(
                    `${msg} On the robot: pip3 install -r Neo_nav2/share/requirements-nav_config_api.txt && sudo ./install_systemcrv.sh && curl -s http://127.0.0.1:12406/nav2/health`
                );
            } finally {
                setLoading(false);
            }
        },
        [loadProfileContent, refreshNavStatus]
    );

    useEffect(() => {
        if (!apiReady || !baseUrl || navServiceState !== "starting") {
            return;
        }
        const intervalId = window.setInterval(() => {
            void refreshNavStatus();
        }, 2000);
        return () => window.clearInterval(intervalId);
    }, [apiReady, baseUrl, navServiceState, refreshNavStatus]);

    useEffect(() => {
        if (!baseUrl) {
            setApiReady(false);
            setError("Nav2 params API URL unavailable");
            return;
        }
        void reloadAll(ACTIVE_PROFILE_FILE);
    }, [baseUrl, reloadAll]);

    const confirmDiscard = useCallback(() => {
        if (!dirty) return true;
        return window.confirm("Discard unsaved changes?");
    }, [dirty]);

    const handleSelectProfile = useCallback(
        async (name: string) => {
            if (name === selectedName) return;
            if (!confirmDiscard()) return;
            await loadProfileContent(name);
        },
        [confirmDiscard, loadProfileContent, selectedName]
    );

    const handleSave = useCallback(async () => {
        const url = baseUrlRef.current;
        if (!url || !selectedName) return;
        setBusy(true);
        try {
            let synced: string | null = null;
            if (isActiveSelection) {
                const res = await putNav2ParamsActive(url, draft);
                synced = res.synced_profile ?? null;
            }
            const stem = profileNameStem(selectedName);
            try {
                const named = await putNav2ParamsNamedProfile(url, stem, draft);
                synced = named.name ?? profileFileName(stem);
            } catch (namedErr) {
                if (!isActiveSelection) {
                    throw namedErr;
                }
            }
            setSavedContent(draft);
            addToast({
                type: "success",
                message: `Saved ${synced ?? stem}`,
            });
            const list = await fetchNav2ParamsProfiles(url);
            setProfiles(list.profiles);
        } catch (e) {
            addToast({
                type: "error",
                message: e instanceof Error ? e.message : "Save failed",
            });
        } finally {
            setBusy(false);
        }
    }, [addToast, draft, isActiveSelection, selectedName]);

    const handleSaveAs = useCallback(async () => {
        const url = baseUrlRef.current;
        if (!url) return;
        const name = window.prompt("Profile name (without path):", "custom");
        if (!name?.trim()) return;
        setBusy(true);
        try {
            const res = await postNav2ParamsProfile(url, name.trim(), draft);
            addToast({ type: "success", message: `Saved profile ${res.name ?? name}` });
            const list = await fetchNav2ParamsProfiles(url);
            setProfiles(list.profiles);
            if (res.name) await loadProfileContent(res.name);
        } catch (e) {
            addToast({
                type: "error",
                message: e instanceof Error ? e.message : "Save as failed",
            });
        } finally {
            setBusy(false);
        }
    }, [addToast, draft, loadProfileContent]);

    const handleActivate = useCallback(async () => {
        const url = baseUrlRef.current;
        if (!url || isActiveSelection) return;
        if (!window.confirm(`Activate ${selectedName}? This replaces nav2_params.yaml.`)) return;
        setBusy(true);
        try {
            const res = await postNav2ParamsActivate(url, selectedName);
            const activatedName =
                res.profile_name ??
                (res.activated ? profileNameStem(res.activated) : profileNameStem(selectedName));
            setActiveProfileName(activatedName);
            addToast({ type: "success", message: `Activated ${profileNameStem(selectedName)}` });
            await reloadAll(selectedName);
        } catch (e) {
            addToast({
                type: "error",
                message: e instanceof Error ? e.message : "Activate failed",
            });
        } finally {
            setBusy(false);
        }
    }, [addToast, isActiveSelection, reloadAll, selectedName]);

    const handleRename = useCallback(async () => {
        const url = baseUrlRef.current;
        if (!url || isActiveSelection) return;
        const toName = window.prompt("Rename to:", profileNameStem(selectedName));
        if (!toName?.trim()) return;
        setBusy(true);
        try {
            const res = await postNav2ParamsRename(url, selectedName, toName.trim());
            addToast({ type: "success", message: `Renamed to ${res.name ?? toName}` });
            const list = await fetchNav2ParamsProfiles(url);
            setProfiles(list.profiles);
            if (res.name) await loadProfileContent(res.name);
        } catch (e) {
            addToast({
                type: "error",
                message: e instanceof Error ? e.message : "Rename failed",
            });
        } finally {
            setBusy(false);
        }
    }, [addToast, isActiveSelection, loadProfileContent, selectedName]);

    const handleDelete = useCallback(async () => {
        const url = baseUrlRef.current;
        if (!url || isActiveSelection) return;
        const stem = profileNameStem(selectedName);
        if (
            !window.confirm(
                `Delete profile "${stem}"? This cannot be undone.`
            )
        ) {
            return;
        }
        setBusy(true);
        try {
            await deleteNav2ParamsProfile(url, selectedName);
            addToast({ type: "success", message: `Deleted profile ${stem}` });
            const list = await fetchNav2ParamsProfiles(url);
            setProfiles(list.profiles);
            const resolvedActive = resolveActiveProfileName(list.profiles);
            setActiveProfileName(resolvedActive);
            const nextSelection = selectionForActiveProfile(list.profiles, resolvedActive);
            await loadProfileContent(nextSelection, {
                profiles: list.profiles,
                activeProfileName: resolvedActive,
            });
        } catch (e) {
            addToast({
                type: "error",
                message: e instanceof Error ? e.message : "Delete failed",
            });
        } finally {
            setBusy(false);
        }
    }, [addToast, isActiveSelection, loadProfileContent, selectedName]);

    const handleRestoreDefault = useCallback(async () => {
        const url = baseUrlRef.current;
        if (!url) return;
        const activeName = activeProfileName || "the active profile";
        if (
            !window.confirm(
                `Restore '${activeName}' from profiles_origin? This overwrites the profile and active nav2_params.yaml. Current active file will be backed up. Navigation is not restarted.`
            )
        ) {
            return;
        }
        setBusy(true);
        try {
            const res = await postNav2ParamsRestoreDefault(url);
            const content = res.content ?? "";
            const restoredName = res.profile_name ?? activeProfileName ?? "";
            setDraft(content);
            setSavedContent(content);
            if (restoredName) {
                setActiveProfileName(restoredName);
                setSelectedName(selectionForActiveProfile(profilesRef.current, restoredName));
            }
            addToast({
                type: "success",
                message: restoredName
                    ? `Restored '${restoredName}' from profiles_origin`
                    : "Restored profile from profiles_origin",
            });
            const list = await fetchNav2ParamsProfiles(url);
            setProfiles(list.profiles);
        } catch (e) {
            addToast({
                type: "error",
                message: e instanceof Error ? e.message : "Restore failed",
            });
        } finally {
            setBusy(false);
        }
    }, [activeProfileName, addToast]);

    const handleRestartNav = useCallback(async () => {
        const url = baseUrlRef.current;
        if (!url) return;
        if (
            !window.confirm(
                "Restart nav.service? Navigation will stop briefly and YAML changes will take effect."
            )
        ) {
            return;
        }
        setBusy(true);
        try {
            await postNav2NavRestart(url);
            setNavServiceState("starting");
            addToast({ type: "success", message: "Restarting nav.service…" });
            for (let attempt = 0; attempt < 90; attempt += 1) {
                await new Promise((resolve) => window.setTimeout(resolve, 2000));
                const status = await fetchNav2NavStatus(url);
                const nextState = navServiceDisplayState(status);
                setNavServiceState(nextState);
                if (nextState === "running" || nextState === "stopped") {
                    break;
                }
            }
        } catch (e) {
            addToast({
                type: "error",
                message: e instanceof Error ? e.message : "Restart failed",
            });
        } finally {
            setBusy(false);
        }
    }, [addToast]);

    const navStatusLabel =
        navServiceState === "unknown"
            ? "Unknown"
            : navServiceState === "running"
              ? "Running"
              : navServiceState === "starting"
                ? "Starting…"
                : "Stopped";

    const navStatusClassName =
        navServiceState === "running"
            ? "text-primary2"
            : navServiceState === "starting"
              ? "text-warning"
              : "text-black40";

    return (
        <section className="mt-8 max-w-4xl border-t border-black80 pt-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h3 className="text-h5 font-semibold text-primary2">Nav2 params (YAML)</h3>
                    <p className="mt-0.5 text-h5 text-black40">
                        Save writes `gb.yaml` / `lynx.yaml` / … and the live file if that profile is active · restart `nav.service` to apply
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-h5 text-black40">nav.service:</span>
                    <span className={`text-h5 font-semibold ${navStatusClassName}`}>
                        {navStatusLabel}
                    </span>
                </div>
            </div>

            {error ? <p className="mt-3 text-h5 text-warning">{error}</p> : null}
            {apiReady && baseUrl ? (
                <p className="mt-1 text-h5 text-black40">API: {baseUrl}</p>
            ) : null}

            <div className="mt-4 flex flex-wrap items-end gap-2">
                <label className="flex min-w-[12rem] flex-1 flex-col gap-1">
                    <span className="text-h5 text-black40">Profile</span>
                    <select
                        value={selectedName}
                        disabled={loading || busy || !apiReady}
                        onChange={(e) => void handleSelectProfile(e.target.value)}
                        className="rounded-r5 border border-black70 bg-black90 px-2 py-1 text-h5 text-white disabled:opacity-50"
                    >
                        {dropdownProfiles.map((profile) => {
                            const stem = profileNameStem(profile.name);
                            const isActive =
                                activeProfileName !== null && stem === activeProfileName;
                            return (
                                <option key={profile.name} value={profile.name}>
                                    {stem}
                                    {isActive ? " (active)" : ""}
                                </option>
                            );
                        })}
                    </select>
                </label>
                <button
                    type="button"
                    disabled={loading || busy || !baseUrl || !apiReady}
                    onClick={() => void reloadAll()}
                    className="rounded-r5 border border-black70 bg-black90 px-3 py-1 text-h5 text-white hover:bg-black80 disabled:opacity-50"
                >
                    Reload
                </button>
            </div>

            <textarea
                value={draft}
                disabled={loading || busy || !baseUrl || !apiReady}
                onChange={(e) => setDraft(e.target.value)}
                spellCheck={false}
                className="mt-3 h-[min(28rem,50vh)] w-full resize-y rounded-r10 border border-black80 bg-black100 p-3 font-mono text-[12px] leading-relaxed text-white disabled:opacity-50"
            />

            {dirty ? (
                <div className="mt-2 space-y-1">
                    <p className="text-h5 text-warning">
                        Unsaved changes
                        {lineDiff.length > 0
                            ? ` · ${lineDiff.length} line${lineDiff.length === 1 ? "" : "s"}`
                            : ""}
                    </p>
                    {visibleLineDiff.length > 0 ? (
                        <pre className="max-h-48 overflow-auto rounded-r5 border border-black80 bg-black100 p-2 font-mono text-[11px] leading-snug text-black20">
                            {visibleLineDiff.map((entry, idx) => (
                                <div
                                    key={`${entry.type}-${entry.line}-${idx}`}
                                    className={
                                        entry.type === "add" ? "text-primary2" : "text-warning"
                                    }
                                >
                                    <span className="inline-block w-10 shrink-0 text-black40">
                                        L{entry.line}
                                    </span>
                                    <span className="mr-1">{entry.type === "add" ? "+" : "−"}</span>
                                    <span className="whitespace-pre-wrap break-all">
                                        {entry.text || " "}
                                    </span>
                                </div>
                            ))}
                            {hiddenDiffCount > 0 ? (
                                <div className="mt-1 text-black40">
                                    …and {hiddenDiffCount} more line
                                    {hiddenDiffCount === 1 ? "" : "s"}
                                </div>
                            ) : null}
                        </pre>
                    ) : null}
                </div>
            ) : null}

            <div className="mt-3 flex flex-wrap gap-2">
                <button
                    type="button"
                    disabled={!baseUrl || !apiReady || busy || loading || !selectedName || !dirty}
                    onClick={() => void handleSave()}
                    className="rounded-r5 border border-primary2 bg-primary2 px-3 py-1 text-h5 font-semibold text-white hover:bg-primary1 disabled:opacity-50"
                >
                    Save
                </button>
                <button
                    type="button"
                    disabled={!baseUrl || !apiReady || busy || loading}
                    onClick={() => void handleSaveAs()}
                    className="rounded-r5 border border-black70 bg-black90 px-3 py-1 text-h5 text-white hover:bg-black80 disabled:opacity-50"
                >
                    Save as…
                </button>
                <button
                    type="button"
                    disabled={!baseUrl || !apiReady || busy || loading || isActiveSelection}
                    onClick={() => void handleActivate()}
                    className="rounded-r5 border border-black70 bg-black90 px-3 py-1 text-h5 text-white hover:bg-black80 disabled:opacity-50"
                >
                    Activate
                </button>
                <button
                    type="button"
                    disabled={!baseUrl || !apiReady || busy || loading || isActiveSelection}
                    onClick={() => void handleRename()}
                    className="rounded-r5 border border-black70 bg-black90 px-3 py-1 text-h5 text-white hover:bg-black80 disabled:opacity-50"
                >
                    Rename…
                </button>
                <button
                    type="button"
                    disabled={!baseUrl || !apiReady || busy || loading || isActiveSelection}
                    onClick={() => void handleDelete()}
                    className="rounded-r5 border border-warning bg-black90 px-3 py-1 text-h5 font-semibold text-warning hover:bg-black80 disabled:opacity-50"
                >
                    Delete
                </button>
                <button
                    type="button"
                    disabled={!baseUrl || !apiReady || busy || loading}
                    onClick={() => void handleRestoreDefault()}
                    className="rounded-r5 border border-black70 bg-black90 px-3 py-1 text-h5 text-white hover:bg-black80 disabled:opacity-50"
                >
                    Restore default
                </button>
                <button
                    type="button"
                    disabled={!baseUrl || !apiReady || busy || loading}
                    onClick={() => void handleRestartNav()}
                    className="ml-auto rounded-r5 border border-warning bg-black90 px-3 py-1 text-h5 font-semibold text-warning hover:bg-black80 disabled:opacity-50"
                >
                    Restart nav.service
                </button>
            </div>
        </section>
    );
};

export default DeveloperNav2ParamsPanel;
